// material
import {
	Button,
	Chip,
	ChipProps,
	Container,
	Grid,
	PaletteBrand,
	Tooltip,
	Typography,
} from '@mui/material';

// types
import { ActionButton } from '../../../@types';

// ui
import { BtnMenu } from '../BtnMenu/BtnMenu';
import { BtnSplit } from '../BtnSplit/BtnSplit';
import { ChipStatus, ChipStatusProps } from '../ChipStatus/ChipStatus';
import { IconCircle, IconCircleProps } from '../IconCircle/IconCircle';
import { MenuItemProps } from '../Menu/Menu';

// props
export interface TitleProps {
	actions?: ActionButton[];
	ChipProps?: ChipProps;
	ChipStatusProps?: ChipStatusProps;
	color?: keyof PaletteBrand;
	IconCircleProps?: IconCircleProps;
	id?: string;
	overline?: string;
	title: string | undefined;
}

export const Title = ({
	actions = [],
	ChipProps,
	ChipStatusProps,
	color = 'grey',
	IconCircleProps,
	id,
	overline,
	title,
}: TitleProps) => {
	const renderChip = () => {
		if (ChipProps) {
			return <Chip {...ChipProps} />;
		} else if (ChipStatusProps) {
			return <ChipStatus {...ChipStatusProps} />;
		}
	};

	// define menu items
	const btnMenuItems: MenuItemProps[] = [];

	for (let i = 0; i < actions.length; i++) {
		const btnActions = actions[i].BtnProps?.actions || [];

		if (btnActions.length) {
			for (let o = 0; o < btnActions.length; o++) {
				btnMenuItems.push({
					children: btnActions[o].children,
					onClick: btnActions[o].onClick,
				});
			}
		} else {
			btnMenuItems.push({
				children: actions[i].children,
				onClick: actions[i].onClick,
			});
		}
	}

	return (
		<Container maxWidth={false}>
			<Grid alignItems="center" container justifyContent={{ md: 'space-between' }} spacing={2}>
				<Grid item xs={12} md={true}>
					<Grid container spacing={1}>
						{overline && (
							<Grid item xs={12}>
								<Grid alignItems="center" container spacing={1}>
									{IconCircleProps && (
										<Grid item>
											<IconCircle size="xs" {...IconCircleProps} />
										</Grid>
									)}
									<Grid item>
										<Typography
											component="div"
											sx={(theme) => {
												return {
													color: theme.palette.brand[color as keyof PaletteBrand][500],
												};
											}}
											variant="overline">
											{overline}
										</Typography>
									</Grid>
								</Grid>
							</Grid>
						)}

						<Grid item xs={12}>
							<Grid
								alignItems="center"
								container
								justifyContent={{ lg: 'space-between' }}
								spacing={1}>
								<Grid item xs={12}>
									<Typography variant="h1">{title}</Typography>
								</Grid>
								{(id || ChipProps || ChipStatusProps) && (
									<Grid item xs={12}>
										<Grid alignItems="center" container spacing={1}>
											{id && (
												<Grid item xs="auto">
													<Typography
														fontWeight={600}
														sx={(theme) => {
															return {
																color: theme.palette.brand[color as keyof PaletteBrand][500],
															};
														}}
														variant="subtitle1">
														{id}
													</Typography>
												</Grid>
											)}
											{(ChipProps || ChipStatusProps) && (
												<Grid item xs="auto">
													{renderChip()}
												</Grid>
											)}
										</Grid>
									</Grid>
								)}
							</Grid>
						</Grid>
					</Grid>
				</Grid>
				{actions.length > 0 && (
					<Grid
						item
						xs={12}
						md="auto"
						sx={(theme) => {
							return {
								display: {
									xs: 'none',
									lg: 'inline-flex',
								},
							};
						}}>
						<Grid container justifyContent="flex-end" spacing={2}>
							{actions.map((action, i) => {
								const { BtnProps, children, ...btnProps } = action;

								let btn = BtnProps ? (
									<BtnSplit {...BtnProps} fullWidth={true} />
								) : (
									<Button {...btnProps} fullWidth>
										{children}
									</Button>
								);

								if (action.TooltipProps) {
									btn = (
										<Tooltip {...action.TooltipProps}>
											<span>{btn}</span>
										</Tooltip>
									);
								}

								return (
									<Grid item key={`title-action-${i}`} xs={12} md="auto">
										{btn}
									</Grid>
								);
							})}
						</Grid>
					</Grid>
				)}
				{btnMenuItems.length > 0 && (
					<Grid
						item
						xs="auto"
						sx={(theme) => {
							return {
								display: {
									lg: 'none',
								},
								position: 'absolute',
								top: theme.spacing(2),
								right: theme.spacing(2),
							};
						}}>
						<BtnMenu items={btnMenuItems} />
					</Grid>
				)}
			</Grid>
		</Container>
	);
};
